// Гибридные утилиты для работы с блогами
// Работают только с SQL API на сервере

const API_BASE = import.meta.env.VITE_API_URL || 'https://stressfrei-hypnose.com/api/blogs';

// Получить все блоги
export const getBlogsFromAPI = async (language = null, status = null) => {
  try {
    let url = API_BASE;
    const params = new URLSearchParams();
    
    if (language) params.append('language', language);
    if (status) params.append('status', status);
    
    if (params.toString()) {
      url += '?' + params.toString();
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blogs = await response.json();
    console.log('Загружены блоги из SQL:', blogs.length);
    return blogs;
  } catch (error) {
    console.error('Ошибка при загрузке блогов из API:', error);
    return [];
  }
};

// Получить блог по ID
export const getBlogById = async (id) => {
  try {
    const response = await fetch(`${API_BASE}/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Ошибка при загрузке блога:', error);
    return null;
  }
};

// Получить блог по slug
export const getBlogBySlug = async (slug) => {
  try {
    const blogs = await getBlogsFromAPI();
    return blogs.find(blog => blog.slug === slug);
  } catch (error) {
    console.error('Ошибка при загрузке блога по slug:', error);
    return null;
  }
};

// Добавить новый блог
export const addBlog = async (blogData) => {
  try {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(blogData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const newBlog = await response.json();
    console.log('Блог создан в SQL:', newBlog.id);
    return newBlog;
  } catch (error) {
    console.error('Ошибка при создании блога через API:', error);
    throw error;
  }
};

// Обновить блог
export const updateBlog = async (blogId, blogData) => {
  try {
    const response = await fetch(`${API_BASE}/${blogId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(blogData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка при обновлении блога:', error);
    throw error;
  }
};

// Удалить блог
export const deleteBlog = async (blogId) => {
  try {
    const response = await fetch(`${API_BASE}/${blogId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка при удалении блога:', error);
    throw error;
  }
};

// Переключить статус блога
export const toggleBlogStatus = async (blogId) => {
  try {
    const blog = await getBlogById(blogId);
    if (!blog) throw new Error('Блог не найден');
    
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    return await updateBlog(blogId, { ...blog, status: newStatus });
  } catch (error) {
    console.error('Ошибка при переключении статуса:', error);
    throw error;
  }
};

// Переключить рекомендуемый статус
export const toggleBlogFeatured = async (blogId) => {
  try {
    const blog = await getBlogById(blogId);
    if (!blog) throw new Error('Блог не найден');
    
    const newFeatured = !blog.featured;
    return await updateBlog(blogId, { ...blog, featured: newFeatured });
  } catch (error) {
    console.error('Ошибка при переключении рекомендуемого статуса:', error);
    throw error;
  }
};

// Получить статистику блогов
export const getBlogStats = async () => {
  try {
    const blogs = await getBlogsFromAPI();
    
    const stats = {
      total: blogs.length,
      published: blogs.filter(blog => blog.status === 'published').length,
      draft: blogs.filter(blog => blog.status === 'draft').length,
      featured: blogs.filter(blog => blog.featured).length,
      totalViews: blogs.reduce((sum, blog) => sum + (blog.views || 0), 0),
      totalLikes: blogs.reduce((sum, blog) => sum + (blog.likes || 0), 0)
    };
    
    return stats;
  } catch (error) {
    console.error('Ошибка при получении статистики:', error);
    return {
      total: 0,
      published: 0,
      draft: 0,
      featured: 0,
      totalViews: 0,
      totalLikes: 0
    };
  }
};

// Получить статистику по языкам
export const getLanguageStats = async () => {
  try {
    const blogs = await getBlogsFromAPI();
    
    const stats = {};
    blogs.forEach(blog => {
      const lang = blog.language || 'ru';
      if (!stats[lang]) {
        stats[lang] = { total: 0, published: 0, draft: 0 };
      }
      stats[lang].total++;
      if (blog.status === 'published') {
        stats[lang].published++;
      } else {
        stats[lang].draft++;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Ошибка при получении статистики по языкам:', error);
    return {};
  }
};

// Получить все теги
export const getAllTags = async () => {
  try {
    const blogs = await getBlogsFromAPI();
    const allTags = new Set();
    
    blogs.forEach(blog => {
      if (blog.tags) {
        const tags = Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim());
        tags.forEach(tag => allTags.add(tag));
      }
    });
    
    return Array.from(allTags);
  } catch (error) {
    console.error('Ошибка при получении тегов:', error);
    return [];
  }
};

// Получить блоги по языку
export const getBlogsByLanguage = async (language) => {
  try {
    return await getBlogsFromAPI(language);
  } catch (error) {
    console.error('Ошибка при получении блогов по языку:', error);
    return [];
  }
};

// Получить доступные языки
export const getAvailableLanguages = async () => {
  try {
    const stats = await getLanguageStats();
    return Object.keys(stats);
  } catch (error) {
    console.error('Ошибка при получении доступных языков:', error);
    return ['ru', 'de'];
  }
};

// Увеличить просмотры блога
export const incrementBlogViews = async (blogId) => {
  try {
    const response = await fetch(`${API_BASE}/${blogId}/view`, {
      method: 'PUT',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка при увеличении просмотров:', error);
    throw error;
  }
};